import { Injectable, computed, effect, inject, signal } from '@angular/core';

import { STORAGE_KEYS } from '../config/ui.constants';
import { AppNotification, AppNotificationProvider } from '../models/notification-center.model';
import { ApprovalNotificationsService } from './approval-notifications.service';
import { AuthService } from './auth.service';

/** Un aviso con lo único que la bandeja agrega por su cuenta: si ya se leyó. */
export interface NotificationItem extends AppNotification {
    read: boolean;
}

/**
 * Bandeja de notificaciones del usuario — FUENTE ÚNICA.
 *
 * Junta lo que publican las fuentes (hoy solo las aprobaciones), las ordena de lo más
 * reciente a lo más viejo y recuerda cuáles ya se leyeron. Cada fuente se encarga de su
 * propio sondeo; aquí no se consulta ningún API.
 *
 * Las leídas se guardan en el navegador, por usuario: no viajan a otro equipo ni a otro
 * navegador. El día que exista un API de notificaciones, se cambia esta persistencia sin
 * tocar la campanita.
 */
@Injectable({ providedIn: 'root' })
export class NotificationCenterService {
    private readonly auth = inject(AuthService);

    /** Las fuentes de la bandeja. Agregar una es agregarla a esta lista. */
    private readonly providers: readonly AppNotificationProvider[] = [inject(ApprovalNotificationsService)];

    private readonly leidas = signal<ReadonlySet<string>>(new Set<string>());

    /** Correo del usuario en sesión: las leídas se guardan por persona. */
    private readonly usuario = computed(() => String(this.auth.currentUser()?.email ?? '').trim().toLowerCase());

    /** Todos los avisos vigentes, del más reciente al más viejo. */
    readonly notifications = computed<readonly NotificationItem[]>(() => {
        const leidas = this.leidas();
        return this.providers
            .flatMap((provider) => provider.notifications())
            .map((aviso) => ({ ...aviso, read: leidas.has(aviso.id) }))
            .sort((izquierda, derecha) => fecha(derecha.occurredAt) - fecha(izquierda.occurredAt));
    });

    readonly unreadCount = computed(() => this.notifications().filter((aviso) => !aviso.read).length);
    readonly total = computed(() => this.notifications().length);
    readonly loading = computed(() => this.providers.some((provider) => provider.loading()));
    readonly failed = computed(() => this.providers.some((provider) => provider.failed()));
    readonly hayNoLeidas = computed(() => this.unreadCount() > 0);

    /**
     * Todas las fuentes ya respondieron al menos una vez.
     *
     * Hasta entonces un cero no significa «no hay avisos», sino «todavía no se sabe»; quien
     * saluda al entrar tiene que esperar a esto para no saludar con la bandeja a medio llenar.
     */
    readonly listo = computed(() => this.providers.every((provider) => provider.consultado()));

    constructor() {
        // Al cambiar de usuario (entrar, salir, otra sesión) se cargan SUS leídas.
        effect(() => {
            this.leidas.set(leerDelNavegador(this.usuario()));
        });
    }

    /** Vuelve a consultar todas las fuentes. */
    refresh(): void {
        this.providers.forEach((provider) => provider.refresh());
    }

    markRead(id: string): void {
        if (this.leidas().has(id)) {
            return;
        }
        this.guardar(new Set([...this.leidas(), id]));
    }

    markAllRead(): void {
        this.guardar(new Set(this.notifications().map((aviso) => aviso.id)));
    }

    /**
     * Guarda las leídas podadas a los avisos vigentes: así la lista no crece sin fin con
     * ids de avisos que ya no existen.
     */
    private guardar(leidas: ReadonlySet<string>): void {
        const vigentes = new Set(this.notifications().map((aviso) => aviso.id));
        const podadas = new Set([...leidas].filter((id) => vigentes.has(id)));
        this.leidas.set(podadas);
        escribirEnNavegador(this.usuario(), podadas);
    }
}

function fecha(valor: string): number {
    return new Date(valor).getTime() || 0;
}

/** Sin usuario no hay clave: no se mezcla lo leído de dos personas en un mismo equipo. */
function claveDe(usuario: string): string {
    return usuario ? `${STORAGE_KEYS.notificationsRead}:${usuario}` : '';
}

function leerDelNavegador(usuario: string): ReadonlySet<string> {
    const clave = claveDe(usuario);
    if (!clave) {
        return new Set<string>();
    }
    try {
        const crudo = localStorage.getItem(clave);
        const ids: unknown = crudo ? JSON.parse(crudo) : [];
        return new Set(Array.isArray(ids) ? ids.filter((id): id is string => typeof id === 'string') : []);
    } catch {
        // Modo privado, almacenamiento lleno o un JSON corrupto: se empieza de cero.
        return new Set<string>();
    }
}

function escribirEnNavegador(usuario: string, leidas: ReadonlySet<string>): void {
    const clave = claveDe(usuario);
    if (!clave) {
        return;
    }
    try {
        localStorage.setItem(clave, JSON.stringify([...leidas]));
    } catch {
        // Que no se pueda recordar lo leído no debe romper la bandeja.
    }
}
